import { useEffect, useRef } from 'react'

import { isEqualExcludeFunctions } from '../widgets/utils/equality'

/**
 * Calls callback when dependencies deeply change.
 *
 * Runs asynchronously after the component has rendered.
 */
export const useOnChange = (callback: () => void, deps: any[]) => {
  const prevDeps = useRef<any[]>(deps)

  useEffect(() => {
    if (isEqualExcludeFunctions(prevDeps.current, deps)) return

    prevDeps.current = deps

    callback()
  }, deps)
}

export const useEffectDeep = (callback: () => void, deps: any[]) => {
  // empty initial state causes this to run on first call.
  const prevDeps = useRef<any[]>([])

  useEffect(() => {
    if (isEqualExcludeFunctions(prevDeps.current, deps)) return

    prevDeps.current = deps

    callback()
  }, deps)
}

/**
 * Calls memo when dependencies deeply change.
 *
 * Runs synchronously
 */
export const useDeepMemo = <T,>(callback: () => T, deps: any[]) => {
  const prevDeps = useRef(deps)
  const state = useRef<T>(callback())

  if (!isEqualExcludeFunctions(prevDeps.current, deps)) {
    prevDeps.current = deps
    state.current = callback()
  }

  return state.current
}

/**
 * Calls callback when dependencies deeply change.
 *
 * Runs synchronously during render.
 */
export const useOnChangeSynchronous = (callback: () => void, deps: any[]) => {
  const prevDeps = useRef<any[]>([])

  if (!isEqualExcludeFunctions(prevDeps.current, deps)) {
    prevDeps.current = deps
    callback()
  }
}
